import { JSX, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import useBuilderStore from "src/store/builderStore";
import {
  DashboardByCodeResponse,
  DashboardListResponse,
  DashboardPageResponse,
} from "src/types/dashboard";
import {
  deleteDashboardApi,
  getDashboardByCodeApi,
  updateDashboardNameApi,
} from "src/api/dashboardApi";
import { usePortalMe } from "src/hooks/usePortal";
import { usePortalAuthStore } from "src/store/portalAuthStore";

import { useNavigate } from "react-router-dom";

type SubHeaderProps = {
  onAddNewDashboard?: () => void;
  onSaveLayout?: () => void;
  isSavingLayout?: boolean;
  dashboardTitleOverride?: string | null;
  isReadOnly?: boolean;
  onCreateCopy?: () => void;
  onDashboardCodeChange?: (newCode: string) => void;
};

export default function SubHeader({
  onAddNewDashboard,
  onSaveLayout,
  isSavingLayout = false,
  dashboardTitleOverride = null,
  isReadOnly = false,
  onCreateCopy,
  onDashboardCodeChange,
}: SubHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const portalToken = usePortalAuthStore((s) => s.token);
  const { data: portalMe } = usePortalMe(!!portalToken);
  const isSuperAdmin = portalMe?.role === "super_admin";
  const hydratedDashboardCodeRef = useRef<string | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSaveLayoutModalOpen, setIsSaveLayoutModalOpen] = useState(false);
  const [editableTitle, setEditableTitle] = useState("");
  const [titleValidationMessage, setTitleValidationMessage] = useState<
    string | null
  >(null);
  const queryClient = useQueryClient();
  const selectedDashboardCode = useBuilderStore(
    (state) => state.selectedDashboardCode,
  );
  const syncDashboard = useBuilderStore((state) => state.syncDashboard);
  const resetBuilderStore = useBuilderStore((state) => state.resetBuilderStore);
  const { filledSlots, totalSlots } = useBuilderStore(
    (state) => state.slotInfo,
  );
  const assignedSlotCountLabel = `${filledSlots}/${totalSlots} slots`;

  const { data: dashboardResponse } = useQuery<DashboardPageResponse, Error>({
    queryKey: ["dashboard_name", selectedDashboardCode],
    queryFn: async () => getDashboardByCodeApi(selectedDashboardCode as string),
    enabled: !!selectedDashboardCode,
    staleTime: 0,
  });
  const dashboardTitle =
    dashboardResponse?.dashboard?.name ??
    dashboardTitleOverride ??
    "New Dashboard";

  const { mutate: updateDashboardName, isPending: isUpdatingDashboardName } =
    useMutation({
      mutationFn: async (name: string) => {
        if (!selectedDashboardCode) {
          throw new Error("No dashboard selected.");
        }

        return updateDashboardNameApi<{
          status: string;
          dashboard?: { code?: string; name?: string };
        }>(selectedDashboardCode, { name });
      },
      onSuccess: async (data) => {
        const newCode = data?.dashboard?.code;
        if (newCode && newCode !== selectedDashboardCode) {
          onDashboardCodeChange?.(newCode);
        }
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["dashboard_name"],
            exact: false,
          }),
          queryClient.invalidateQueries({
            queryKey: ["dashboards"],
          }),
        ]);
        setIsEditingTitle(false);
        setTitleValidationMessage(null);
        toast.success("Dashboard name updated successfully.");
      },
      onError: (error: Error) => {
        toast.error(error.message || "Failed to update dashboard name.");
      },
    });

  const { mutate: deleteDashboard, isPending: isDeletingDashboard } =
    useMutation({
      mutationFn: async () => {
        if (!selectedDashboardCode) {
          throw new Error("No dashboard selected.");
        }

        return deleteDashboardApi(selectedDashboardCode);
      },
      onSuccess: async () => {
        const deletedDashboardCode = selectedDashboardCode;

        const cached = queryClient.getQueryData<DashboardListResponse>([
          "dashboards",
        ]);
        const remaining =
          cached?.my_dashboards?.filter(
            (d) => d.code !== deletedDashboardCode,
          ) ?? [];
        const nextCode =
          remaining[0]?.code ?? cached?.dashboards?.[0]?.code ?? null;

        resetBuilderStore();
        setIsDeleteModalOpen(false);
        setIsEditingTitle(false);
        setEditableTitle("");
        setTitleValidationMessage(null);

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["dashboards"],
          }),
          queryClient.invalidateQueries({
            queryKey: ["dashboard_name", deletedDashboardCode],
          }),
          queryClient.invalidateQueries({
            queryKey: ["dashboard_templates", deletedDashboardCode],
          }),
          queryClient.invalidateQueries({
            queryKey: ["builder_dashboard_data", deletedDashboardCode],
          }),
          queryClient.invalidateQueries({
            queryKey: ["individual_widget_data", deletedDashboardCode],
          }),
        ]);

        toast.success("Dashboard deleted successfully.");
        navigate(nextCode ? `/builder/${nextCode}` : "/builder");
      },
      onError: (error: Error) => {
        toast.error(error.message || "Failed to delete dashboard.");
      },
    });

  useEffect(() => {
    const responseDashboardCode = dashboardResponse?.dashboard?.code ?? null;

    if (
      dashboardResponse &&
      responseDashboardCode &&
      hydratedDashboardCodeRef.current !== responseDashboardCode
    ) {
      syncDashboard(
        dashboardResponse as unknown as DashboardByCodeResponse | null,
      );
      hydratedDashboardCodeRef.current = responseDashboardCode;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardResponse]);

  useEffect(() => {
    if (!selectedDashboardCode) {
      hydratedDashboardCodeRef.current = null;
    }
  }, [selectedDashboardCode]);

  useEffect(() => {
    if (!isEditingTitle) {
      setEditableTitle(dashboardTitle);
      setTitleValidationMessage(null);
    }
  }, [dashboardTitle, isEditingTitle]);

  const handleStartEditTitle = () => {
    if (!selectedDashboardCode || isUpdatingDashboardName) {
      return;
    }

    setEditableTitle(dashboardTitle);
    setTitleValidationMessage(null);
    setIsEditingTitle(true);
  };

  const handleCancelEditTitle = () => {
    setEditableTitle(dashboardTitle);
    setTitleValidationMessage(null);
    setIsEditingTitle(false);
  };

  const handleSubmitTitle = () => {
    const trimmedTitle = editableTitle.trim();

    if (!trimmedTitle) {
      setTitleValidationMessage("Dashboard name is required.");
      return;
    }

    if (trimmedTitle.length < 3) {
      setTitleValidationMessage(
        "Dashboard name must be at least 3 characters.",
      );
      return;
    }

    if (trimmedTitle === dashboardTitle) {
      setIsEditingTitle(false);
      setTitleValidationMessage(null);
      return;
    }

    updateDashboardName(trimmedTitle);
  };

  const handleDeleteDashboard = () => {
    if (!selectedDashboardCode || isDeletingDashboard) {
      return;
    }

    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    if (isDeletingDashboard) {
      return;
    }

    setIsDeleteModalOpen(false);
  };

  const handleConfirmDeleteDashboard = () => {
    deleteDashboard();
  };

  const handleSaveLayoutClick = () => {
    if (isSavingLayout || !selectedDashboardCode) {
      return;
    }

    setIsSaveLayoutModalOpen(true);
  };

  const handleCloseSaveLayoutModal = () => {
    if (isSavingLayout) {
      return;
    }

    setIsSaveLayoutModalOpen(false);
  };

  const handleConfirmSaveLayout = () => {
    setIsSaveLayoutModalOpen(false);
    onSaveLayout?.();
  };

  const canEdit = !isReadOnly || isSuperAdmin;

  return (
    <div className="d-flex align-items-center">
      <div className="col-md-7 col-12">
        <div className="builder-title-row">
          {canEdit && isEditingTitle ? (
            <div
              className="builder-title-edit"
              role="group"
              aria-label="Edit dashboard name"
            >
              <input
                type="text"
                className={`form-control form-control-sm builder-title-edit__input ${
                  titleValidationMessage ? "is-invalid" : ""
                }`}
                value={editableTitle}
                onChange={(event) => {
                  setEditableTitle(event.target.value);
                  if (titleValidationMessage) {
                    setTitleValidationMessage(null);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleSubmitTitle();
                  }

                  if (event.key === "Escape") {
                    event.preventDefault();
                    handleCancelEditTitle();
                  }
                }}
                placeholder="Enter dashboard name"
                maxLength={120}
                autoFocus
                disabled={isUpdatingDashboardName}
                aria-label="Dashboard name"
              />
              <button
                className="btn btn-sm btn-success"
                type="button"
                onClick={handleSubmitTitle}
                disabled={isUpdatingDashboardName}
                aria-label="Save dashboard name"
                title="Save name"
              >
                {isUpdatingDashboardName ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <i className="bi bi-check-lg" />
                )}
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                type="button"
                onClick={handleCancelEditTitle}
                disabled={isUpdatingDashboardName}
                aria-label="Cancel editing dashboard name"
                title="Cancel"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
          ) : (
            <div className="builder-title-display">
              <h3 id="builder-title" className="me-2 mb-0 fw-bold text-primary">{dashboardTitle}</h3>
              {canEdit && (
                <button
                  className="btn btn-sm btn-light builder-title-display__edit"
                  type="button"
                  onClick={handleStartEditTitle}
                  disabled={!selectedDashboardCode || isUpdatingDashboardName}
                  aria-label="Edit dashboard name"
                  title="Edit dashboard name"
                >
                  <i className="bi bi-pencil-square" />
                </button>
              )}
            </div>
          )}
          {titleValidationMessage ? (
            <span className="builder-title-validation">
              {titleValidationMessage}
            </span>
          ) : null}

          <span className="builder-slot-pill">{assignedSlotCountLabel}</span>
        </div>
      </div>
      <div className="col-md-5 col-12">
        <div className="d-flex gap-2 justify-content-end">
          {isReadOnly && !isSuperAdmin ? (
            <>
              <button
                className="btn btn-sm px-2 btn-primary"
                type="button"
                onClick={onAddNewDashboard}
              >
                Add New Dashboard
              </button>
              <button
                className="btn btn-sm px-2 btn-primary"
                type="button"
                onClick={onCreateCopy}
                disabled={!selectedDashboardCode}
              >
                <i className="bi bi-copy me-1" />
                Save As
              </button>
            </>
          ) : (
            <>
              {!isSuperAdmin && (
                <button
                  className="btn btn-sm px-2 btn-primary"
                  type="button"
                  onClick={onAddNewDashboard}
                >
                  Add New Dashboard
                </button>
              )}
              <button
                className="btn btn-sm px-2 btn-outline-success"
                type="button"
                onClick={handleSaveLayoutClick}
                disabled={isSavingLayout || !selectedDashboardCode}
              >
                {isSavingLayout ? "Saving..." : "Save Layout"}
              </button>
              {!isSuperAdmin && (
                <button
                  className="btn btn-sm px-2 btn-outline-danger"
                  type="button"
                  onClick={handleDeleteDashboard}
                  disabled={!selectedDashboardCode || isDeletingDashboard}
                >
                  {isDeletingDashboard ? "Deleting..." : "Delete"}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <Modal show={isDeleteModalOpen} centered onHide={handleCloseDeleteModal}>
        <Modal.Header closeButton={!isDeletingDashboard}>
          <Modal.Title>Delete Dashboard</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this dashboard? It will be moved to
          the Recycle Bin and can be reactivated later.
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleCloseDeleteModal}
            disabled={isDeletingDashboard}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleConfirmDeleteDashboard}
            disabled={isDeletingDashboard}
          >
            {isDeletingDashboard ? "Deleting..." : "Delete"}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={isSaveLayoutModalOpen} centered onHide={handleCloseSaveLayoutModal}>
        <Modal.Header closeButton={!isSavingLayout}>
          <Modal.Title>Save Layout</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to save the layout?</Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleCloseSaveLayoutModal}
            disabled={isSavingLayout}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConfirmSaveLayout}
            disabled={isSavingLayout}
          >
            {isSavingLayout ? "Saving..." : "Save"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
